import { useEffect } from 'react';
import { useCookieConsent } from './useCookieConsent';

declare global {
  interface Window {
    clarity: (...args: any[]) => void;
  }
}

export const useClarity = () => {
  const { consent } = useCookieConsent();

  useEffect(() => {
    const CLARITY_ID = import.meta.env.VITE_CLARITY_PROJECT_ID;
    
    if (!CLARITY_ID || CLARITY_ID === 'your_clarity_project_id' || typeof window === 'undefined') {
      return;
    }

    // Only initialize Clarity if analytics consent is given
    if (consent.analytics && !document.querySelector(`script[src*="clarity.ms"]`)) {
      // Defer Clarity loading until after page is interactive
      const loadClarity = () => {
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.innerHTML = `
          (function(c,l,a,r,i,t,y){
            c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
            t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
            y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
          })(window, document, "clarity", "script", "${CLARITY_ID}");
        `;
        document.head.appendChild(script);
      };

      // Defer loading using requestIdleCallback or setTimeout
      if ('requestIdleCallback' in window) {
        requestIdleCallback(loadClarity);
      } else {
        setTimeout(loadClarity, 2000);
      }
    }
  }, [consent.analytics]);

  const clarityIdentify = (userId: string, customTags?: Record<string, string>) => {
    if (!consent.analytics || typeof window === 'undefined' || !window.clarity) {
      return;
    }

    window.clarity('identify', userId, customTags);
  };

  const claritySetTag = (key: string, value: string) => {
    if (!consent.analytics || typeof window === 'undefined' || !window.clarity) {
      return;
    }

    window.clarity('set', key, value);
  };

  return { clarityIdentify, claritySetTag };
};
